import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Flame, Crown } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { userProgress } = useApp();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      // Use mock data if API fails
      setLeaderboard([
        { user_id: "user1", username: "Carlos_SP", total_xp: 5200, level: 11, streak: 15 },
        { user_id: "user2", username: "Maria_Nagoya", total_xp: 4800, level: 10, streak: 12 },
        { user_id: "user3", username: "João_Tokyo", total_xp: 4500, level: 10, streak: 8 },
        { user_id: "user4", username: "Ana_Osaka", total_xp: 4200, level: 9, streak: 20 },
        { user_id: "user5", username: "Pedro_Aichi", total_xp: 3900, level: 8, streak: 5 },
        { user_id: "user6", username: "Lucia_Gunma", total_xp: 3600, level: 8, streak: 10 },
        { user_id: "user7", username: "Rafael_Shizuoka", total_xp: 3300, level: 7, streak: 7 },
        { user_id: "user8", username: "Fernanda_Saitama", total_xp: 3000, level: 7, streak: 3 },
        { user_id: "user9", username: "Bruno_Kanagawa", total_xp: 2700, level: 6, streak: 14 },
        { user_id: "current", username: "Você", total_xp: userProgress.totalXP, level: userProgress.level, streak: userProgress.currentStreak },
      ].sort((a, b) => b.total_xp - a.total_xp));
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="text-sm font-bold text-muted-foreground">{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-yellow-500/5 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-400/5 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-600/5 border-amber-600/30';
      default:
        return 'bg-card';
    }
  };

  // Find current user's rank
  const currentUserRank = leaderboard.findIndex(u => u.username === 'Você') + 1;

  return (
    <Layout showBack title="Ranking">
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto">
            <Trophy className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="font-display text-2xl font-bold">Ranking Global</h1>
          <p className="text-muted-foreground">Compete com outros estudantes</p>
        </div>

        {/* Current User Position */}
        {currentUserRank > 0 && (
          <div className="card-elevated p-4 border-primary/30 bg-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold">#{currentUserRank}</span>
                </div>
                <div>
                  <p className="font-semibold">Sua Posição</p>
                  <p className="text-sm text-muted-foreground">{userProgress.totalXP} XP</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-accent">
                <Flame className="w-4 h-4" />
                <span className="font-semibold">{userProgress.currentStreak}</span>
              </div>
            </div>
          </div>
        )}

        {/* Top 3 Podium */}
        <div className="flex items-end justify-center gap-2 py-4">
          {/* 2nd Place */}
          {leaderboard[1] && (
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-gray-400/20 flex items-center justify-center mb-2">
                <span className="text-xl">🥈</span>
              </div>
              <p className="text-xs font-medium truncate w-20 text-center">{leaderboard[1].username}</p>
              <p className="text-xs text-muted-foreground">{leaderboard[1].total_xp} XP</p>
              <div className="w-16 h-16 bg-gray-400/20 rounded-t-lg mt-2" />
            </div>
          )}
          
          {/* 1st Place */}
          {leaderboard[0] && (
            <div className="flex flex-col items-center">
              <Crown className="w-6 h-6 text-yellow-500 mb-1" />
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mb-2">
                <span className="text-2xl">🥇</span>
              </div>
              <p className="text-sm font-semibold truncate w-24 text-center">{leaderboard[0].username}</p>
              <p className="text-xs text-muted-foreground">{leaderboard[0].total_xp} XP</p>
              <div className="w-20 h-24 bg-yellow-500/20 rounded-t-lg mt-2" />
            </div>
          )}
          
          {/* 3rd Place */}
          {leaderboard[2] && (
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-amber-600/20 flex items-center justify-center mb-2">
                <span className="text-xl">🥉</span>
              </div>
              <p className="text-xs font-medium truncate w-20 text-center">{leaderboard[2].username}</p>
              <p className="text-xs text-muted-foreground">{leaderboard[2].total_xp} XP</p>
              <div className="w-16 h-12 bg-amber-600/20 rounded-t-lg mt-2" />
            </div>
          )}
        </div>

        {/* Full Leaderboard */}
        <div className="space-y-2">
          {leaderboard.slice(3).map((user, index) => {
            const rank = index + 4;
            const isCurrentUser = user.username === 'Você';
            
            return (
              <div
                key={user.user_id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-xl border',
                  isCurrentUser ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-sm font-bold text-muted-foreground">{rank}</span>
                  </div>
                  <div>
                    <p className={cn('font-medium text-sm', isCurrentUser && 'text-primary')}>
                      {user.username}
                    </p>
                    <p className="text-xs text-muted-foreground">Nível {user.level}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-sm">{user.total_xp} XP</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Flame className="w-3 h-3 text-accent" />
                    {user.streak}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div className="p-4 rounded-xl bg-muted/50 text-center">
          <p className="text-sm text-muted-foreground">
            🏆 Continue estudando para subir no ranking!
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default LeaderboardPage;
